import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet,
  Alert, Switch, ActivityIndicator, Modal, useWindowDimensions, Image,
} from 'react-native';
import {
  createProduct, getProducts, getProductsByRestaurant,
  updateProduct, deleteProduct, uploadProductImage, ProductData,
} from '../query/storeProductService';
import { getRestaurantsByBranch } from '../query/restaurantService';
import { CategoryType } from '../components/CreateProduct';

// Categories live INSIDE the restaurant doc (zapee_restaurant_partner), under
// `categories` (or legacy `menuCategories` / `categoryList` / `menu.categories`).
// Each entry is a string or a map {id, title/tittle/name}.
const extractCategories = (r: any): CategoryType[] => {
  if (!r) return [];
  let raw: any[] = [];
  for (const k of ['categories', 'menuCategories', 'categoryList']) {
    if (Array.isArray(r[k])) { raw = r[k]; break; }
  }
  if (raw.length === 0 && r.menu && Array.isArray(r.menu.categories)) raw = r.menu.categories;
  return raw
    .map((c: any, i: number): CategoryType => {
      if (typeof c === 'string') return { id: c, tittle: c };
      return {
        id: String(c.id ?? c.categoryId ?? c.tittle ?? c.title ?? c.name ?? i),
        tittle: String(c.title ?? c.tittle ?? c.name ?? c.label ?? c.category ?? c.id ?? ''),
      };
    })
    .filter(c => c.id);
};

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', dangerLight: '#fee2e2',
  bg: '#0E1512', card: '#ffffff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

const selectStyle: React.CSSProperties = {
  width: '100%', height: 46, backgroundColor: '#fafafa',
  border: '1px solid #e2e8f0', borderRadius: 10,
  paddingLeft: 14, paddingRight: 14, fontSize: 14,
  color: '#E2EDE6', cursor: 'pointer', outline: 'none',
};

const darkSelectStyle: React.CSSProperties = {
  height: 34, backgroundColor: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(33,128,141,0.3)', borderRadius: 8,
  paddingLeft: 10, paddingRight: 10, fontSize: 12,
  color: '#e2f0f2', cursor: 'pointer', outline: 'none', minWidth: 140,
};

interface Props { branchId?: string; }

const emptyProduct: ProductData = {
  tittle: '', description: '', image: '', featureImages: [],
  price: '', discount: '', stock: '', category: '', itemcategory: '',
  branch: '', available: true, show: true,
  isVeg: true, bestSeller: false, latest: false,
  variants: [], timestamp: '',
};

// Defined outside to prevent TextInput focus loss on re-render
const F = ({ label, value, onChange, keyType, multiline }: any) => (
  <View style={ms.formGroup}>
    <Text style={ms.label}>{label}</Text>
    <TextInput
      style={[ms.input, multiline && { height: 80 }]}
      placeholder={label} value={String(value ?? '')}
      onChangeText={onChange} keyboardType={keyType || 'default'}
      multiline={multiline} placeholderTextColor={C.textMuted}
    />
  </View>
);

export default function ProductManager({ branchId = '' }: Props) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const imgInputRef = useRef<HTMLInputElement>(null);

  const [products, setProducts] = useState<ProductData[]>([]);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState<ProductData>({ ...emptyProduct });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState<CategoryType[]>([]);

  // Variant editor (drawer)
  const [vName, setVName] = useState('');
  const [vPrice, setVPrice] = useState('');

  // Bulk price adjustment (per selected restaurant)
  const [priceModal, setPriceModal] = useState(false);
  const [priceMode, setPriceMode] = useState<'increase' | 'decrease'>('increase');
  const [pricePct, setPricePct] = useState('');
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceProgress, setPriceProgress] = useState('');

  const onChangeField = <K extends keyof ProductData>(field: K, value: ProductData[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    getRestaurantsByBranch(branchId)
      .then(setRestaurants)
      .catch(console.error);
  }, [branchId]);

  // Categories come from the restaurant doc(s): the selected restaurant's
  // categories, or all restaurants' categories merged when none is selected.
  useEffect(() => {
    if (!restaurants.length) { setCategories([]); return; }
    if (filterRestaurant) {
      const r = restaurants.find(
        (x: any) => (x.storeuid || x.uid || x.docId) === filterRestaurant
      );
      setCategories(extractCategories(r));
    } else {
      const byId = new Map<string, CategoryType>();
      for (const r of restaurants) {
        for (const c of extractCategories(r)) byId.set(c.id, c);
      }
      setCategories(Array.from(byId.values()));
    }
  }, [restaurants, filterRestaurant]);

  useEffect(() => {
    loadProducts();
  }, [filterRestaurant]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = filterRestaurant
        ? await getProductsByRestaurant(filterRestaurant)
        : await getProducts();
      setProducts(data);
    } catch {
      Alert.alert('Error', 'Failed to load products');
    }
    setLoading(false);
  };

  const selectedRestaurantName = () =>
    restaurants.find((r: any) => (r.storeuid || r.uid || r.docId) === filterRestaurant)?.storename || 'this restaurant';

  // Bulk-increase (or decrease, with a negative %) every loaded product's price.
  const applyBulkPrice = async () => {
    const pct = Math.abs(parseFloat(pricePct));
    if (isNaN(pct) || pct === 0) { window.alert('Enter a non-zero percentage'); return; }
    const targets = products.filter(p => p.id);
    if (targets.length === 0) { window.alert('No products to update'); return; }
    setPriceBusy(true);
    const factor = priceMode === 'increase' ? 1 + pct / 100 : 1 - pct / 100;
    try {
      let done = 0;
      for (let i = 0; i < targets.length; i += 10) {
        const chunk = targets.slice(i, i + 10);
        await Promise.all(chunk.map(p => {
          const cur = parseFloat(String(p.price ?? '0')) || 0;
          const next = Math.max(0, Math.round(cur * factor));
          return updateProduct(p.id!, { price: String(next) });
        }));
        done += chunk.length;
        setPriceProgress(`Updated ${done}/${targets.length}…`);
      }
      setPriceProgress('');
      setPriceModal(false);
      setPricePct('');
      await loadProducts();
      window.alert(`${priceMode === 'increase' ? 'Increased' : 'Decreased'} ${targets.length} product prices by ${pct}%`);
    } catch {
      setPriceProgress('');
      window.alert('Bulk price update failed');
    } finally {
      setPriceBusy(false);
    }
  };

  const handleImagePick = async () => {
    imgInputRef.current?.click();
  };

  const handleImageFileChange = async (e: any) => {
    const file: File = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const tempId = editingId || `new_${Date.now()}`;
      const url = await uploadProductImage(file, tempId);
      onChangeField('image', url);
    } catch {
      Alert.alert('Error', 'Image upload failed');
    }
    setImgUploading(false);
    e.target.value = '';
  };

  const openModalForEdit = (product: ProductData & { id?: string }) => {
    setFormData(product);
    setEditingId(product.id || null);
    setModalVisible(true);
  };

  const openModalForCreate = () => {
    setFormData({ ...emptyProduct, branch: filterRestaurant });
    setEditingId(null);
    setModalVisible(true);
  };

  const addVariant = () => {
    const name = vName.trim();
    const price = parseFloat(vPrice);
    if (!name || isNaN(price)) { window.alert('Enter a variant name and price'); return; }
    const v = {
      variantId: 'v' + Date.now(),
      name,
      price,
      isDefault: (formData.variants ?? []).length === 0,
      available: true,
      pricing: { sellingPrice: price, mrp: price, currency: 'INR' },
    };
    onChangeField('variants', [ ...(formData.variants ?? []), v ]);
    setVName(''); setVPrice('');
  };

  const removeVariant = (idx: number) => {
    onChangeField('variants', (formData.variants ?? []).filter((_, i) => i !== idx));
  };

  const onSubmit = async () => {
    if (!formData.tittle?.trim()) { Alert.alert('Validation', 'Product title is required'); return; }
    try {
      const payload = { ...formData, timestamp: formData.timestamp || new Date().toISOString() };
      if (editingId) {
        await updateProduct(editingId, payload);
      } else {
        await createProduct(payload);
      }
      setModalVisible(false);
      loadProducts();
      Alert.alert('Success', `Product ${editingId ? 'updated' : 'created'} successfully`);
    } catch {
      Alert.alert('Error', 'Failed to save product');
    }
  };

  const onDelete = async (item: ProductData & { id?: string }) => {
    // Alert.alert with buttons is a no-op on react-native-web — use the browser
    // confirm dialog so delete actually works on the web admin.
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete "${item.tittle}"? This cannot be undone.`)
      : false;
    if (!ok) return;
    try {
      await deleteProduct(item.id!, item.image, item.featureImages);
      loadProducts();
    } catch {
      window.alert('Failed to delete product');
    }
  };

  const filteredProducts = products.filter(item => {
    const titleMatch =
      !searchQuery || (item.tittle && item.tittle.toLowerCase().includes(searchQuery.toLowerCase()));
    const categoryMatch = !filterCategory || item.category === filterCategory;
    return titleMatch && categoryMatch;
  });


  return (
    <View style={styles.container}>
      {/* Toolbar: title + search + filters + actions */}
      <View style={styles.filtersRow}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1, flexShrink: 0 }}>inventory_2</span>
        <View style={{ marginRight: 8 }}>
          <Text style={styles.toolbarTitle}>Products</Text>
          <Text style={styles.toolbarSub}>Manage product catalogue</Text>
        </View>
        <View style={[styles.searchContainer, { flex: 1 }]}>
          <Text style={styles.searchIcon}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Search by title..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#8BAAB8"
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Text style={{ color: '#8BAAB8', padding: 4 }}>✕</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <select
          value={filterRestaurant}
          onChange={(e: any) => { setFilterRestaurant(e.target.value); setFilterCategory(''); }}
          style={darkSelectStyle}
        >
          <option value="">All Restaurants</option>
          {restaurants.map((r: any) => (
            <option key={r.docId} value={r.storeuid || r.uid || r.docId}>
              {r.storename || r.name || r.docId}
            </option>
          ))}
        </select>

        <select
          value={filterCategory}
          onChange={(e: any) => setFilterCategory(e.target.value)}
          style={darkSelectStyle}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.tittle}</option>
          ))}
        </select>

        {filterRestaurant ? (
          <>
            <TouchableOpacity style={styles.incBtn} onPress={() => { setPriceMode('increase'); setPricePct(''); setPriceModal(true); }}>
              <Text style={styles.bulkPriceBtnText}>↑ %</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.decBtn} onPress={() => { setPriceMode('decrease'); setPricePct(''); setPriceModal(true); }}>
              <Text style={styles.bulkPriceBtnText}>↓ %</Text>
            </TouchableOpacity>
          </>
        ) : null}
        <TouchableOpacity style={styles.addBtn} onPress={openModalForCreate}>
          <Text style={styles.addBtnText}>+ Add Product</Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={C.primary} />
          <Text style={styles.loadingText}>Loading products...</Text>
        </View>
      ) : filteredProducts.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyIcon}>📦</Text>
          <Text style={styles.emptyTitle}>No products found</Text>
          <Text style={styles.emptySubtitle}>Add your first product to get started</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.grid}>
          {filteredProducts.map((item, index) => {
            const restaurant = restaurants.find(
              r => (r.storeuid || r.uid || r.docId) === item.branch
            );
            const discountPct = item.price && item.discount
              ? Math.round((parseFloat(item.discount) / parseFloat(item.price)) * 100)
              : 0;
            return (
              <View key={item.id || index} style={[styles.card, isMobile && styles.cardFull]}>
                {/* Product image */}
                <View style={styles.cardImageWrap}>
                  {item.image ? (
                    <Image source={{ uri: item.image }} style={styles.cardImage} resizeMode="cover" />
                  ) : (
                    <View style={[styles.cardImage, styles.cardImagePlaceholder]}>
                      <Text style={{ fontSize: 36 }}>📦</Text>
                    </View>
                  )}
                  {/* Status badge overlay */}
                  <View style={[styles.statusOverlay, { backgroundColor: item.available ? C.success : '#94a3b8' }]}>
                    <Text style={styles.statusOverlayText}>{item.available ? 'Active' : 'Off'}</Text>
                  </View>
                  {discountPct > 0 && (
                    <View style={styles.discountBadge}>
                      <Text style={styles.discountText}>{discountPct}% off</Text>
                    </View>
                  )}
                </View>

                {/* Card body */}
                <View style={styles.cardBody}>
                  <Text style={styles.cardTitle} numberOfLines={2}>{item.tittle || 'Untitled'}</Text>

                  {restaurant ? (
                    <View style={styles.restaurantRow}>
                      <Text style={styles.restaurantIcon}>🏪</Text>
                      <Text style={styles.restaurantName} numberOfLines={1}>
                        {restaurant.storename || restaurant.name}
                      </Text>
                    </View>
                  ) : null}

                  {item.category ? (
                    <Text style={styles.categoryTag} numberOfLines={1}>{item.category}</Text>
                  ) : null}

                  {/* Price row */}
                  <View style={styles.priceRow}>
                    <Text style={styles.priceMain}>₹{item.price || '0'}</Text>
                    {item.discount && parseFloat(item.discount) > 0 ? (
                      <Text style={styles.priceOriginal}>
                        ₹{(parseFloat(item.price || '0') + parseFloat(item.discount)).toFixed(0)}
                      </Text>
                    ) : null}
                    <View style={styles.stockBadge}>
                      <Text style={styles.stockText}>Stock: {item.stock || '—'}</Text>
                    </View>
                  </View>

                  {/* Variants */}
                  {item.variants && item.variants.length > 0 ? (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {item.variants.slice(0, 4).map((v, i) => (
                        <View
                          key={v.variantId || i}
                          style={{ backgroundColor: C.primaryLight, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}
                        >
                          <Text style={{ color: C.primary, fontSize: 11, fontWeight: '600' }}>
                            {v.name} · ₹{v.price}
                          </Text>
                        </View>
                      ))}
                      {item.variants.length > 4 ? (
                        <Text style={{ color: C.textMuted, fontSize: 11, alignSelf: 'center' }}>
                          +{item.variants.length - 4}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}

                  {/* Flags */}
                  <View style={styles.flagsRow}>
                    {item.isVeg
                      ? <View style={[styles.flag, { backgroundColor: '#dcfce7' }]}><Text style={[styles.flagText, { color: '#15803d' }]}>Veg</Text></View>
                      : <View style={[styles.flag, { backgroundColor: '#fee2e2' }]}><Text style={[styles.flagText, { color: '#b91c1c' }]}>Non-veg</Text></View>}
                    {item.bestSeller && <View style={[styles.flag, { backgroundColor: '#fef9c3' }]}><Text style={[styles.flagText, { color: '#b45309' }]}>Bestseller</Text></View>}
                    {item.latest && <View style={[styles.flag, { backgroundColor: '#ede9fe' }]}><Text style={[styles.flagText, { color: '#7c3aed' }]}>New</Text></View>}
                  </View>
                </View>

                {/* Actions */}
                <View style={styles.cardActions}>
                  <TouchableOpacity style={styles.editBtn} onPress={() => openModalForEdit(item)}>
                    <Text style={styles.editBtnText}>Edit</Text>
                  </TouchableOpacity>
                  {item.id && (
                    <TouchableOpacity style={styles.deleteBtn} onPress={() => onDelete(item)}>
                      <Text style={styles.deleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          })}
        </ScrollView>
      )}

      {/* Edit / Create — right drawer */}
      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <View style={{ flex: 1, flexDirection: 'row', backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setModalVisible(false)}
          />
          <View
            style={{ width: 480, maxWidth: '100%', height: '100%', backgroundColor: '#17201B', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)' } as any}
          >
        <View style={ms.fullScreen}>
          <View style={ms.fullHeader}>
            <TouchableOpacity onPress={() => setModalVisible(false)} style={ms.backBtn}>
              <Text style={ms.backBtnText}>✕ Cancel</Text>
            </TouchableOpacity>
            <Text style={ms.fullTitle}>{editingId ? 'Edit Product' : 'New Product'}</Text>
            <TouchableOpacity style={ms.saveHeaderBtn} onPress={onSubmit}>
              <Text style={ms.saveHeaderBtnText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={ms.scrollContent} keyboardShouldPersistTaps="handled">
            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={imgInputRef as any}
              style={{ display: 'none' }}
              onChange={handleImageFileChange}
            />

            <Text style={ms.sectionLabel}>Product Image</Text>
            <View style={ms.imageSection}>
              {formData.image ? (
                <Image source={{ uri: formData.image }} style={ms.imagePreview} resizeMode="cover" />
              ) : (
                <View style={ms.imagePlaceholder}>
                  <Text style={{ fontSize: 40 }}>📦</Text>
                  <Text style={{ color: C.textMuted, fontSize: 12, marginTop: 8 }}>No image</Text>
                </View>
              )}
              <TouchableOpacity style={ms.uploadBtn} onPress={handleImagePick} disabled={imgUploading}>
                {imgUploading
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={ms.uploadBtnText}>
                      {formData.image ? 'Change Image' : 'Upload Image'}
                    </Text>
                }
              </TouchableOpacity>
              {formData.image ? (
                <TouchableOpacity style={ms.removeBtn} onPress={() => onChangeField('image', '')}>
                  <Text style={ms.removeBtnText}>Remove</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            <Text style={ms.sectionLabel}>Product Info</Text>
            <F label="Title *" value={formData.tittle} onChange={(t: string) => onChangeField('tittle', t)} />
            <F label="Description" value={formData.description} onChange={(t: string) => onChangeField('description', t)} multiline />
            <F label="Item Category" value={formData.itemcategory} onChange={(t: string) => onChangeField('itemcategory', t)} />

            <Text style={ms.sectionLabel}>Restaurant</Text>
            <View style={ms.formGroup}>
              <Text style={ms.label}>Restaurant</Text>
              <select
                value={formData.branch}
                onChange={(e: any) => onChangeField('branch', e.target.value)}
                style={selectStyle}
              >
                <option value="">— Select Restaurant —</option>
                {restaurants.map((r: any) => (
                  <option key={r.docId} value={r.storeuid || r.uid || r.docId}>
                    {r.storename || r.name || r.docId}
                  </option>
                ))}
              </select>
            </View>

            <Text style={ms.sectionLabel}>Category</Text>
            <View style={ms.formGroup}>
              <Text style={ms.label}>Category</Text>
              <select
                value={formData.category}
                onChange={(e: any) => onChangeField('category', e.target.value)}
                style={selectStyle}
              >
                <option value="">— Select Category —</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.tittle}</option>
                ))}
              </select>
            </View>

            <Text style={ms.sectionLabel}>Pricing & Stock</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 1 }}><F label="Price (₹)" value={formData.price} onChange={(t: string) => onChangeField('price', t)} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Discount (₹)" value={formData.discount} onChange={(t: string) => onChangeField('discount', t)} keyType="numeric" /></View>
            </View>
            <F label="Stock" value={formData.stock} onChange={(t: string) => onChangeField('stock', t)} keyType="numeric" />

            <Text style={ms.sectionLabel}>Variants</Text>
            {(formData.variants ?? []).length === 0 ? (
              <Text style={{ color: C.textMuted, fontSize: 13, marginBottom: 8 }}>
                No variants. Add sizes/options like Small / Medium / Large below.
              </Text>
            ) : (
              (formData.variants ?? []).map((v, i) => (
                <View
                  key={v.variantId || i}
                  style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                           backgroundColor: '#141E18', borderRadius: 10, padding: 10, marginBottom: 8 }}
                >
                  <View>
                    <Text style={{ fontWeight: '700', color: C.textPrimary }}>{v.name}</Text>
                    <Text style={{ color: C.textMuted, fontSize: 12 }}>₹{v.price}{v.isDefault ? '  · default' : ''}</Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => removeVariant(i)}
                    style={{ backgroundColor: C.dangerLight, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 12 }}
                  >
                    <Text style={{ color: C.danger, fontWeight: '700', fontSize: 13 }}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ))
            )}
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
              <View style={{ flex: 2 }}>
                <F label="Variant name" value={vName} onChange={setVName} />
              </View>
              <View style={{ flex: 1 }}>
                <F label="Price (₹)" value={vPrice} onChange={setVPrice} keyType="numeric" />
              </View>
              <TouchableOpacity
                onPress={addVariant}
                style={{ backgroundColor: C.primary, borderRadius: 10, paddingVertical: 13, paddingHorizontal: 18, marginBottom: 14 }}
              >
                <Text style={{ color: '#fff', fontWeight: '700' }}>Add</Text>
              </TouchableOpacity>
            </View>

            <Text style={ms.sectionLabel}>Toggles</Text>
            {[
              { label: 'Available', sub: 'In stock & orderable', field: 'available' },
              { label: 'Show', sub: 'Visible to customers', field: 'show' },
              { label: 'Veg', sub: 'Vegetarian item', field: 'isVeg' },
              { label: 'Bestseller', sub: 'Mark as bestseller', field: 'bestSeller' },
              { label: 'Latest', sub: 'Mark as new/latest', field: 'latest' },
            ].map(({ label, sub, field }) => (
              <View key={field} style={ms.switchRow}>
                <View>
                  <Text style={ms.switchLabel}>{label}</Text>
                  <Text style={ms.switchSubLabel}>{sub}</Text>
                </View>
                <Switch
                  value={(formData as any)[field]}
                  onValueChange={val => onChangeField(field as keyof ProductData, val)}
                  trackColor={{ false: C.border, true: C.primary }}
                  thumbColor="#fff"
                />
              </View>
            ))}
          </ScrollView>
        </View>
          </View>
        </View>
      </Modal>

      {/* Bulk price adjust modal */}
      <Modal visible={priceModal} animationType="fade" transparent>
        <TouchableOpacity activeOpacity={1} style={bp.overlay} onPress={() => !priceBusy && setPriceModal(false)}>
          <TouchableOpacity activeOpacity={1} style={bp.card} onPress={() => {}}>
            <View style={[bp.iconWrap, { backgroundColor: priceMode === 'increase' ? '#fef3c7' : '#fee2e2' }]}>
              <Text style={{ fontSize: 24 }}>{priceMode === 'increase' ? '↑' : '↓'}</Text>
            </View>
            <Text style={bp.title}>{priceMode === 'increase' ? 'Increase' : 'Decrease'} Product Prices</Text>
            <Text style={bp.body}>
              {priceMode === 'increase' ? 'Increase' : 'Decrease'} all <Text style={{ fontWeight: '800', color: '#21808D' }}>{products.length}</Text> products of {selectedRestaurantName()}.
            </Text>
            <Text style={bp.label}>{priceMode === 'increase' ? 'Increase' : 'Decrease'} by (%)</Text>
            <TextInput
              style={bp.input}
              value={pricePct}
              onChangeText={setPricePct}
              keyboardType="decimal-pad"
              placeholder="e.g. 10"
              placeholderTextColor="#94a3b8"
            />
            <Text style={bp.note}>
              New price = current price {priceMode === 'increase' ? '×' : '×'} ({priceMode === 'increase' ? '1 + %/100' : '1 − %/100'}), rounded to the nearest ₹. This cannot be undone in bulk.
            </Text>
            {priceBusy && priceProgress ? <Text style={bp.progress}>{priceProgress}</Text> : null}
            <View style={bp.actions}>
              <TouchableOpacity style={bp.cancel} onPress={() => setPriceModal(false)} disabled={priceBusy}>
                <Text style={bp.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[bp.apply, { backgroundColor: priceMode === 'increase' ? '#f59e0b' : '#ef4444' }, priceBusy && { opacity: 0.6 }]}
                onPress={applyBulkPrice} disabled={priceBusy}
              >
                {priceBusy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={bp.applyTxt}>{priceMode === 'increase' ? 'Increase' : 'Decrease'}</Text>}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0E1512', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  headerMobile: { flexDirection: 'column', alignItems: 'flex-start', gap: 12 },
  pageTitle: { fontSize: 24, fontWeight: '700', color: '#E2EDE6' },
  pageSubtitle: { fontSize: 13, color: '#7A9A80', marginTop: 2 },
  addBtn: { backgroundColor: '#4ECDC4', paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8 },
  addBtnText: { color: '#0A1712', fontWeight: '700', fontSize: 12 },
  incBtn: { backgroundColor: 'rgba(245,158,11,0.18)', borderWidth: 1, borderColor: '#f59e0b', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, justifyContent: 'center' },
  decBtn: { backgroundColor: 'rgba(239,68,68,0.15)', borderWidth: 1, borderColor: '#ef4444', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, justifyContent: 'center' },
  bulkPriceBtnText: { color: '#fff', fontWeight: '700', fontSize: 12 },

  filtersRow: { flexDirection: 'row', gap: 10, alignItems: 'center', marginHorizontal: -16, marginTop: -16, marginBottom: 16, backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)', flexWrap: 'wrap' },
  toolbarTitle: { fontSize: 14, fontWeight: '800', color: '#fff' },
  toolbarSub: { fontSize: 10, color: '#8BAAB8', marginTop: 1 },
  searchContainer: {
    flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 8, borderWidth: 1, borderColor: 'rgba(33,128,141,0.25)', paddingHorizontal: 10, height: 36, minWidth: 160,
  },
  searchIcon: { fontSize: 13, marginRight: 6, color: '#8BAAB8' },
  searchInput: { flex: 1, fontSize: 13, color: '#e2f0f2', outlineStyle: 'none' } as any,
  restaurantFilter: { minWidth: 180 },

  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  loadingText: { marginTop: 12, color: '#7A9A80' },

  grid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 14, paddingBottom: 60,
  },

  card: {
    width: 220, backgroundColor: '#17201B', borderRadius: 18,
    shadowColor: '#000', shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08, shadowRadius: 8, elevation: 4,
    overflow: 'hidden',
  },
  cardFull: { width: '100%' },

  cardImageWrap: { position: 'relative' },
  cardImage: { width: '100%', height: 150, backgroundColor: '#f1f5f9' },
  cardImagePlaceholder: { justifyContent: 'center', alignItems: 'center' },

  statusOverlay: {
    position: 'absolute', top: 10, left: 10,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8,
  },
  statusOverlayText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  discountBadge: {
    position: 'absolute', top: 10, right: 10,
    backgroundColor: '#ef4444', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8,
  },
  discountText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  cardBody: { padding: 14 },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#E2EDE6', marginBottom: 6, lineHeight: 20 },

  restaurantRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 4 },
  restaurantIcon: { fontSize: 11 },
  restaurantName: { fontSize: 11, color: '#21808D', fontWeight: '600', flex: 1 },

  categoryTag: {
    alignSelf: 'flex-start', backgroundColor: '#e8f5f7', borderRadius: 6,
    paddingHorizontal: 8, paddingVertical: 3, fontSize: 10,
    color: '#21808D', fontWeight: '600', marginBottom: 8,
  },

  priceRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  priceMain: { fontSize: 16, fontWeight: '800', color: '#E2EDE6' },
  priceOriginal: { fontSize: 12, color: '#5A7A60', textDecorationLine: 'line-through' },
  stockBadge: {
    marginLeft: 'auto', backgroundColor: '#0E1512', borderRadius: 6,
    paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)',
  },
  stockText: { fontSize: 10, color: '#7A9A80', fontWeight: '600' },

  flagsRow: { flexDirection: 'row', gap: 4, flexWrap: 'wrap' },
  flag: {
    backgroundColor: '#d1fae5', borderRadius: 5,
    paddingHorizontal: 7, paddingVertical: 3,
  },
  flagText: { fontSize: 10, fontWeight: '600', color: '#10b981' },

  cardActions: { flexDirection: 'row', gap: 8, padding: 12, paddingTop: 0 },
  editBtn: { flex: 1, backgroundColor: '#e8f5f7', borderRadius: 10, paddingVertical: 9, alignItems: 'center' },
  editBtnText: { color: '#21808D', fontWeight: '700', fontSize: 12 },
  deleteBtn: { flex: 1, backgroundColor: '#fee2e2', borderRadius: 10, paddingVertical: 9, alignItems: 'center' },
  deleteBtnText: { color: '#ef4444', fontWeight: '700', fontSize: 12 },

  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 80 },
  emptyIcon: { fontSize: 56, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#E2EDE6', marginBottom: 6 },
  emptySubtitle: { fontSize: 14, color: '#7A9A80' },
});

const ms = StyleSheet.create({
  fullScreen: { flex: 1, backgroundColor: '#17201B' },
  fullHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0',
  },
  backBtn: { padding: 4 },
  backBtnText: { color: '#7A9A80', fontSize: 15, fontWeight: '500' },
  fullTitle: { fontSize: 17, fontWeight: '700', color: '#E2EDE6' },
  saveHeaderBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 8, borderRadius: 8 },
  saveHeaderBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  scrollContent: { padding: 20, paddingBottom: 40 },
  sectionLabel: {
    fontSize: 13, fontWeight: '700', color: '#21808D', textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 12, marginTop: 8,
  },
  formGroup: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: {
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10,
    paddingHorizontal: 14, paddingVertical: 11, fontSize: 14,
    color: '#E2EDE6', backgroundColor: '#fafafa',
  },
  imageSection: { alignItems: 'center', marginBottom: 16, gap: 10 },
  imagePreview: { width: 160, height: 160, borderRadius: 12, backgroundColor: '#f1f5f9' },
  imagePlaceholder: {
    width: 160, height: 160, borderRadius: 12, backgroundColor: '#f1f5f9',
    justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: 'rgba(255,255,255,0.09)', borderStyle: 'dashed',
  },
  uploadBtn: { backgroundColor: '#21808D', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  uploadBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  removeBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 10 },
  removeBtnText: { color: '#ef4444', fontWeight: '600', fontSize: 13 },
  switchRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9',
  },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#E2EDE6' },
  switchSubLabel: { fontSize: 12, color: '#7A9A80', marginTop: 2 },
});

// ── Bulk price modal styles ───────────────────────────────────────
const bp = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#17201B', borderRadius: 16, padding: 24, width: '100%', maxWidth: 400, shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 10 },
  iconWrap: { width: 52, height: 52, borderRadius: 26, backgroundColor: '#fef3c7', justifyContent: 'center', alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 18, fontWeight: '700', color: '#E2EDE6', marginBottom: 6 },
  body: { fontSize: 13, color: '#7A9A80', lineHeight: 19, marginBottom: 16 },
  label: { fontSize: 12, fontWeight: '700', color: '#A8C4AF', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#E2EDE6', backgroundColor: '#fafafa' },
  note: { fontSize: 11, color: '#5A7A60', marginTop: 8, lineHeight: 15 },
  progress: { fontSize: 12, color: '#21808D', fontWeight: '600', marginTop: 12, textAlign: 'center' },
  actions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancel: { flex: 1, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', alignItems: 'center' },
  cancelTxt: { color: '#E2EDE6', fontWeight: '600', fontSize: 14 },
  apply: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: '#f59e0b', alignItems: 'center' },
  applyTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
